import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import ConfirmModal from '@/components/common/ConfirmModal'
import EmptyState from '@/components/common/EmptyState'
import ErrorState from '@/components/common/ErrorState'
import LoadingState from '@/components/common/LoadingState'
import { ApiError } from '@/api/client'
import {
  createApiKey,
  deleteApiKey,
  fetchApiKeys,
  fetchSiteInventory,
  revokeApiKey,
  rotateApiKey,
  updateApiKeyConfig,
} from './companyDetailApi'
import type {
  ApiKeyConfig,
  ApiKeyRecord,
  CompanyController,
  SiteAccessMode,
  SiteInventoryCompany,
  SubscriptionTier,
} from './companyDetailTypes'
import { formatLocal, graceCountdown } from './apiKeyFormat'

/** Whose keys a section manages: one company's (Company detail page), or
 * global keys (Companies page), which belong to no company and may only
 * reach explicitly selected sites across companies. */
export type ApiKeyScope = { kind: 'client'; clientId: string; controllers: CompanyController[] } | { kind: 'global' }

/**
 * API keys, ported from legacy includes/company_details.html's "API Keys"
 * card and its Generate / Sites / Rotate modals and script, against
 * /portal/clients/api/v2/<client_id>/api-keys (or .../global-api-keys).
 * Behaviour matches legacy:
 *  - columns Name, Key (prefix + dots), Created, Last Used (browser-local),
 *    Expires, Plan, Endpoints, Sites, Status (incl. the grace-period countdown);
 *  - actions by lifecycle_status: active -> Edit/Rotate/Revoke,
 *    grace_period -> Revoke, revoked/expired/grace_expired -> Delete;
 *  - Generate: required label, Standard/Premium plan, endpoint permissions
 *    (daily midnight on, five-minute granular off by default), All or
 *    Selected sites, key revealed once with Copy and the x-api-key hint;
 *  - Edit: plan, endpoints and sites in one save, same secret (the old
 *    "Sites" modal, widened); a 409 means someone else saved first;
 *  - Rotate: grace period 0/3/7 (default)/14/30 days, new key revealed once;
 *  - Revoke/Delete confirm with legacy's own wording.
 * Global keys: no "all sites" option, a cross-company site picker, and a
 * review step before the key is generated.
 * Rendered only for clients.api_keys.manage - the permission every one of
 * these endpoints requires (legacy gated the actions on admin/super user).
 */
export default function ApiKeysSection({ scope }: { scope: ApiKeyScope }) {
  const clientId = scope.kind === 'client' ? scope.clientId : null
  const queryClient = useQueryClient()
  const keysQueryKey = clientId === null ? ['global-api-keys'] : ['company', clientId, 'api-keys']
  const keysQuery = useQuery({ queryKey: keysQueryKey, queryFn: () => fetchApiKeys(clientId) })
  const refreshKeys = () => queryClient.invalidateQueries({ queryKey: keysQueryKey })
  const inventoryQuery = useQuery({ queryKey: ['api-key-site-inventory'], queryFn: fetchSiteInventory, enabled: scope.kind === 'global' })

  const [showGenerate, setShowGenerate] = useState(false)
  const [editKey, setEditKey] = useState<ApiKeyRecord | null>(null)
  const [rotateKey, setRotateKey] = useState<ApiKeyRecord | null>(null)
  const [confirm, setConfirm] = useState<{ kind: 'revoke' | 'delete'; key: ApiKeyRecord } | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)

  const revokeMutation = useMutation({
    mutationFn: (keyId: string) => revokeApiKey(clientId, keyId),
    onSuccess: () => {
      setConfirm(null)
      refreshKeys()
    },
    onError: (err) => {
      setConfirm(null)
      setActionError(`Failed to revoke key: ${errorText(err)}`)
    },
  })
  const deleteMutation = useMutation({
    mutationFn: (keyId: string) => deleteApiKey(clientId, keyId),
    onSuccess: () => {
      setConfirm(null)
      refreshKeys()
    },
    onError: (err) => {
      setConfirm(null)
      setActionError(`Failed to delete key: ${errorText(err)}`)
    },
  })

  const keys = keysQuery.data ?? []
  const siteNames = useMemo(() => siteLabels(scope, inventoryQuery.data), [scope, inventoryQuery.data])

  return (
    <section className="rounded-2xl border border-nav-line bg-card p-5">
      <div className="mb-3 flex items-center justify-between gap-3">
        <div>
          <h2 className="text-base font-semibold text-ink">{scope.kind === 'global' ? 'Global API Keys' : 'API Keys'}</h2>
          {scope.kind === 'global' && (
            <p className="mt-0.5 text-xs text-text-secondary">
              Keys not tied to one company. Each reaches only the sites selected for it.
            </p>
          )}
        </div>
        <button
          type="button"
          onClick={() => setShowGenerate(true)}
          className="shrink-0 rounded-full bg-brand px-4 py-1.5 text-sm font-semibold text-white hover:opacity-90"
        >
          + Generate Key
        </button>
      </div>

      {actionError && (
        <div className="mb-3 flex items-start justify-between gap-3 rounded-xl border border-error px-3 py-2 text-sm text-error" role="alert">
          <span>{actionError}</span>
          <button type="button" onClick={() => setActionError(null)} className="text-xs hover:underline">
            Dismiss
          </button>
        </div>
      )}

      {keysQuery.isLoading && <LoadingState label="Loading API keys…" />}
      {keysQuery.isError && (
        <ErrorState message={`Failed to load API keys: ${errorText(keysQuery.error)}`} onRetry={() => keysQuery.refetch()} />
      )}
      {keysQuery.isSuccess && keys.length === 0 && <EmptyState message="No API keys yet." />}
      {keys.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-[13.5px]">
            <thead>
              <tr className="border-b border-nav-line text-left text-xs text-nav-muted">
                {['Name', 'Key', 'Created', 'Last Used', 'Expires', 'Plan', 'Endpoints', 'Sites', 'Status', ''].map((h, i) => (
                  <th key={i} className="whitespace-nowrap py-3 pr-4 font-semibold">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {keys.map((key) => (
                <tr key={key.id} className="border-b border-nav-line last:border-0">
                  <td className="py-2.5 pr-4 text-ink">{key.name}</td>
                  <td className="whitespace-nowrap py-2.5 pr-4 font-mono text-xs text-text-secondary">
                    {key.key_prefix}
                    {'•'.repeat(8)}
                  </td>
                  <td className="whitespace-nowrap py-2.5 pr-4 text-ink">{key.created_at ?? '-'}</td>
                  <td className="whitespace-nowrap py-2.5 pr-4 text-ink">{formatLocal(key.last_used_at)}</td>
                  <td className="whitespace-nowrap py-2.5 pr-4 text-ink">{key.expires_at ?? 'Never'}</td>
                  <td className="py-2.5 pr-4">
                    <PlanBadge tier={key.subscription_tier} />
                  </td>
                  <td className="py-2.5 pr-4">
                    <EndpointBadges record={key} />
                  </td>
                  <td className="py-2.5 pr-4">
                    <SitesBadge
                      mode={key.site_access_mode}
                      count={key.assigned_controller_ids.length}
                      title={key.assigned_controller_ids.map((id) => siteNames[id] ?? id).join('\n')}
                    />
                  </td>
                  <td className="py-2.5 pr-4">
                    <StatusBadge record={key} />
                  </td>
                  <td className="whitespace-nowrap py-2.5 text-right">
                    <div className="flex justify-end gap-3 text-xs font-semibold">
                      {key.lifecycle_status === 'active' && (
                        <>
                          <ActionButton onClick={() => setEditKey(key)}>Edit</ActionButton>
                          <ActionButton onClick={() => setRotateKey(key)} className="text-accent">
                            Rotate
                          </ActionButton>
                        </>
                      )}
                      {(key.lifecycle_status === 'active' || key.lifecycle_status === 'grace_period') && (
                        <ActionButton onClick={() => setConfirm({ kind: 'revoke', key })} className="text-amber-600 dark:text-amber-400">
                          Revoke
                        </ActionButton>
                      )}
                      {['revoked', 'expired', 'grace_expired'].includes(key.lifecycle_status) && (
                        <ActionButton onClick={() => setConfirm({ kind: 'delete', key })} className="text-error">
                          Delete
                        </ActionButton>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showGenerate && (
        <GenerateKeyDialog
          scope={scope}
          inventory={inventoryQuery.data}
          inventoryError={inventoryQuery.isError}
          onClose={() => setShowGenerate(false)}
          onCreated={refreshKeys}
        />
      )}
      {editKey && (
        <EditKeyDialog
          scope={scope}
          record={editKey}
          inventory={inventoryQuery.data}
          onClose={() => setEditKey(null)}
          onSaved={refreshKeys}
        />
      )}
      {rotateKey && <RotateDialog clientId={clientId} record={rotateKey} onClose={() => setRotateKey(null)} onRotated={refreshKeys} />}

      <ConfirmModal
        isOpen={confirm !== null}
        title={confirm?.kind === 'delete' ? 'Delete API key' : 'Revoke API key'}
        message={
          confirm?.kind === 'delete'
            ? 'Permanently delete this API key record? The key is already inactive but this removes it from view. This cannot be undone.'
            : 'Revoke this API key? Any integrations using it will immediately lose access.'
        }
        confirmLabel={confirm?.kind === 'delete' ? 'Delete' : 'Revoke'}
        isConfirming={revokeMutation.isPending || deleteMutation.isPending}
        onConfirm={() => {
          if (!confirm) return
          if (confirm.kind === 'delete') deleteMutation.mutate(confirm.key.id)
          else revokeMutation.mutate(confirm.key.id)
        }}
        onCancel={() => setConfirm(null)}
      />
    </section>
  )
}

// ---------------------------------------------------------------- helpers

function errorText(err: unknown): string {
  return err instanceof Error ? err.message : 'Unknown error'
}

/** controller_id -> "Site name (Company)" for tooltips and the review step. */
function siteLabels(scope: ApiKeyScope, inventory: SiteInventoryCompany[] | undefined): Record<string, string> {
  const labels: Record<string, string> = {}
  if (scope.kind === 'client') {
    for (const c of scope.controllers) labels[c.controller_id] = c.controller_name || c.controller_id
  }
  for (const company of inventory ?? []) {
    for (const site of company.sites) labels[site.controller_id] = `${site.controller_name} (${company.company_name})`
  }
  return labels
}

const BADGE = 'inline-block whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-semibold'

function PlanBadge({ tier }: { tier: SubscriptionTier }) {
  return tier === 'premium' ? (
    <span className={`${BADGE} bg-brand text-white`}>Premium</span>
  ) : (
    <span className={`${BADGE} bg-bg-secondary text-text-secondary`}>Standard</span>
  )
}

function EndpointBadges({ record }: { record: ApiKeyRecord }) {
  return (
    <div className="flex flex-wrap gap-1">
      {record.daily_access && <span className={`${BADGE} bg-bg-secondary text-text-secondary`}>Daily</span>}
      {record.granular_access && (
        <span className={`${BADGE} bg-violet-100 text-violet-800 dark:bg-violet-900/40 dark:text-violet-300`}>5-min</span>
      )}
    </div>
  )
}

function SitesBadge({ mode, count, title }: { mode: SiteAccessMode; count: number; title?: string }) {
  return mode === 'selected' ? (
    <span className={`${BADGE} bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300`} title={title}>
      {count} Site(s)
    </span>
  ) : (
    <span className={`${BADGE} bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-300`}>All Sites</span>
  )
}

function StatusBadge({ record }: { record: ApiKeyRecord }) {
  const danger = `${BADGE} bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300`
  switch (record.lifecycle_status) {
    case 'active':
      return <span className={`${BADGE} bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300`}>Active</span>
    case 'grace_period':
      return (
        <span
          className={`${BADGE} bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300`}
          title={`Replaced by rotation — valid until ${record.grace_period_ends_at ?? ''}`}
        >
          Grace Period{graceCountdown(record.grace_period_ends_at_iso)}
        </span>
      )
    case 'revoked':
      return <span className={danger}>Revoked</span>
    case 'expired':
      return <span className={danger}>Expired</span>
    case 'grace_expired':
      return <span className={danger}>Grace Ended</span>
    default:
      return <span className={`${BADGE} bg-bg-secondary text-text-secondary`}>Unknown</span>
  }
}

function ActionButton({ onClick, className = 'text-text-primary', children }: { onClick: () => void; className?: string; children: ReactNode }) {
  return (
    <button type="button" onClick={onClick} className={`hover:underline ${className}`}>
      {children}
    </button>
  )
}

const INPUT = 'w-full rounded-xl border border-nav-line bg-input px-3.5 py-2 text-sm text-text-primary focus:border-brand focus:outline-none'
const LABEL = 'mb-1.5 block text-xs font-semibold text-nav-muted'

function Dialog({
  title,
  onClose,
  canClose = true,
  wide = false,
  children,
  footer,
}: {
  title: string
  onClose: () => void
  canClose?: boolean
  wide?: boolean
  children: ReactNode
  footer: ReactNode
}) {
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && canClose) onClose()
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [onClose, canClose])
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4" role="presentation">
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className={`max-h-[90vh] w-full ${wide ? 'max-w-xl' : 'max-w-md'} overflow-y-auto rounded-3xl border border-nav-line bg-card p-6 shadow-[0_10px_28px_rgba(16,40,26,0.16),0_1px_3px_rgba(16,40,26,0.08)]`}
      >
        <h2 className="mb-4 text-base font-semibold text-text-primary">{title}</h2>
        {children}
        <div className="mt-5 flex justify-end gap-3">{footer}</div>
      </div>
    </div>
  )
}

const BTN_SECONDARY =
  'rounded-full border border-nav-line px-5 py-2 text-sm font-semibold text-text-primary hover:bg-nav-hover disabled:opacity-50'
const BTN_PRIMARY = 'rounded-full bg-brand px-5 py-2 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-50'

function SiteAccessFields({
  name,
  mode,
  setMode,
  selected,
  setSelected,
  controllers,
  allLabel,
}: {
  name: string
  mode: SiteAccessMode
  setMode: (m: SiteAccessMode) => void
  selected: string[]
  setSelected: (ids: string[]) => void
  controllers: CompanyController[]
  allLabel: string
}) {
  return (
    <fieldset>
      <legend className={LABEL}>Site Access</legend>
      <label className="flex items-center gap-2 text-sm text-text-primary">
        <input type="radio" name={name} checked={mode === 'all'} onChange={() => setMode('all')} />
        {allLabel}
      </label>
      <label className="mt-1 flex items-center gap-2 text-sm text-text-primary">
        <input type="radio" name={name} checked={mode === 'selected'} onChange={() => setMode('selected')} />
        Selected Sites Only
      </label>
      {mode === 'selected' && (
        <div className="ml-6 mt-2 space-y-1">
          <p className="text-xs text-text-secondary">Select sites this key may access:</p>
          {controllers.length === 0 && <p className="text-xs text-text-secondary">No sites assigned to this client.</p>}
          {controllers.map((c) => (
            <label key={c.controller_id} className="flex items-center gap-2 text-sm text-text-primary">
              <input
                type="checkbox"
                checked={selected.includes(c.controller_id)}
                onChange={(e) =>
                  setSelected(e.target.checked ? [...selected, c.controller_id] : selected.filter((id) => id !== c.controller_id))
                }
              />
              {c.controller_name || c.controller_id}
            </label>
          ))}
        </div>
      )}
    </fieldset>
  )
}

/** Cross-company site picker for global keys. Sites are grouped by company
 * and always show their controller ID, since site names repeat across
 * companies. Selection is by controller_id only. */
function GlobalSitePicker({
  inventory,
  selected,
  setSelected,
}: {
  inventory: SiteInventoryCompany[] | undefined
  selected: string[]
  setSelected: (ids: string[]) => void
}) {
  const [filter, setFilter] = useState('')
  if (!inventory) return <LoadingState label="Loading sites…" />
  const q = filter.trim().toLowerCase()
  const companies = inventory
    .map((company) => ({
      ...company,
      sites: company.sites.filter(
        (s) =>
          !q ||
          company.company_name.toLowerCase().includes(q) ||
          s.controller_name.toLowerCase().includes(q) ||
          s.controller_id.toLowerCase().includes(q),
      ),
    }))
    .filter((company) => company.sites.length > 0)
  const toggle = (id: string, on: boolean) => setSelected(on ? [...selected, id] : selected.filter((x) => x !== id))
  return (
    <fieldset>
      <legend className={LABEL}>
        Authorised sites <span className="text-error">*</span>
      </legend>
      <input
        value={filter}
        onChange={(e) => setFilter(e.target.value)}
        placeholder="Filter by company, site or controller ID…"
        aria-label="Filter sites"
        className={`${INPUT} mb-2`}
      />
      <div className="max-h-64 space-y-3 overflow-y-auto rounded-xl border border-nav-line p-3">
        {companies.length === 0 && <p className="text-xs text-text-secondary">No sites match.</p>}
        {companies.map((company) => (
          <div key={company.company_id}>
            <p className="mb-1 text-xs font-semibold text-nav-muted">{company.company_name}</p>
            {company.sites.map((site) => (
              <label key={site.controller_id} className="flex items-center gap-2 text-sm text-text-primary">
                <input
                  type="checkbox"
                  checked={selected.includes(site.controller_id)}
                  onChange={(e) => toggle(site.controller_id, e.target.checked)}
                />
                <span>{site.controller_name}</span>
                <span className="font-mono text-[11px] text-text-secondary">{site.controller_id}</span>
              </label>
            ))}
          </div>
        ))}
      </div>
      <p className="mt-1 text-xs text-text-secondary">{selected.length} site(s) selected. The key can reach no other site.</p>
    </fieldset>
  )
}

function PlanAndEndpointFields({
  idPrefix,
  tier,
  setTier,
  daily,
  setDaily,
  granular,
  setGranular,
}: {
  idPrefix: string
  tier: SubscriptionTier
  setTier: (t: SubscriptionTier) => void
  daily: boolean
  setDaily: (v: boolean) => void
  granular: boolean
  setGranular: (v: boolean) => void
}) {
  return (
    <>
      <div>
        <label htmlFor={`${idPrefix}-tier`} className={LABEL}>
          Subscription Plan
        </label>
        <select id={`${idPrefix}-tier`} value={tier} onChange={(e) => setTier(e.target.value as SubscriptionTier)} className={INPUT}>
          <option value="standard">Standard — daily summary registers only</option>
          <option value="premium">Premium — full dataset</option>
        </select>
        <p className="mt-1 text-xs text-text-secondary">Controls which registers are accessible via this key, on every endpoint.</p>
      </div>
      <fieldset>
        <legend className={LABEL}>Endpoints</legend>
        <label className="flex items-center gap-2 text-sm text-text-primary">
          <input type="checkbox" checked={daily} onChange={(e) => setDaily(e.target.checked)} />
          Daily midnight data
        </label>
        <label className="mt-1 flex items-center gap-2 text-sm text-text-primary">
          <input type="checkbox" checked={granular} onChange={(e) => setGranular(e.target.checked)} />
          Five-minute granular data
        </label>
        {!daily && !granular && <p className="mt-1 text-xs text-error">Enable at least one endpoint.</p>}
      </fieldset>
    </>
  )
}

function KeyReveal({ rawKey, warning, children }: { rawKey: string; warning: string; children?: ReactNode }) {
  const [copied, setCopied] = useState(false)
  return (
    <div>
      <div className="mb-3 rounded-xl border border-amber-400 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:bg-amber-900/30 dark:text-amber-200">
        <strong>{warning}</strong> It will not be shown again.
      </div>
      <div className="flex gap-2">
        <input readOnly value={rawKey} aria-label="API key" className={`${INPUT} font-mono text-xs`} onFocus={(e) => e.target.select()} />
        <button
          type="button"
          onClick={() => {
            void navigator.clipboard?.writeText(rawKey).then(() => setCopied(true))
          }}
          className={copied ? `${BTN_PRIMARY} px-3` : `${BTN_SECONDARY} px-3`}
        >
          {copied ? 'Copied!' : 'Copy'}
        </button>
      </div>
      {children}
    </div>
  )
}

function ScopeReview({ config, labels }: { config: ApiKeyConfig; labels: Record<string, string> }) {
  const endpoints = [config.daily_access && 'Daily midnight data', config.granular_access && 'Five-minute granular data'].filter(Boolean)
  return (
    <dl className="space-y-2 rounded-xl border border-nav-line p-3 text-sm">
      <div>
        <dt className="text-xs font-semibold text-nav-muted">Plan</dt>
        <dd className="text-text-primary">{config.subscription_tier === 'premium' ? 'Premium — full dataset' : 'Standard — summary registers only'}</dd>
      </div>
      <div>
        <dt className="text-xs font-semibold text-nav-muted">Endpoints</dt>
        <dd className="text-text-primary">{endpoints.join(', ')}</dd>
      </div>
      <div>
        <dt className="text-xs font-semibold text-nav-muted">Sites ({config.controller_ids.length})</dt>
        <dd>
          <ul className="max-h-40 overflow-y-auto text-text-primary">
            {config.controller_ids.map((id) => (
              <li key={id}>
                {labels[id] ?? id} <span className="font-mono text-[11px] text-text-secondary">{id}</span>
              </li>
            ))}
          </ul>
        </dd>
      </div>
    </dl>
  )
}

function GenerateKeyDialog({
  scope,
  inventory,
  inventoryError,
  onClose,
  onCreated,
}: {
  scope: ApiKeyScope
  inventory: SiteInventoryCompany[] | undefined
  inventoryError: boolean
  onClose: () => void
  onCreated: () => void
}) {
  const isGlobal = scope.kind === 'global'
  const [name, setName] = useState('')
  const [nameInvalid, setNameInvalid] = useState(false)
  const [tier, setTier] = useState<SubscriptionTier>('standard')
  const [daily, setDaily] = useState(true)
  const [granular, setGranular] = useState(false)
  const [mode, setMode] = useState<SiteAccessMode>(isGlobal ? 'selected' : 'all')
  const [selected, setSelected] = useState<string[]>([])
  const [reviewing, setReviewing] = useState(false)
  const config: ApiKeyConfig = {
    subscription_tier: tier,
    daily_access: daily,
    granular_access: granular,
    site_access_mode: mode,
    controller_ids: mode === 'selected' ? selected : [],
  }
  const mutation = useMutation({
    mutationFn: () => createApiKey(scope.kind === 'client' ? scope.clientId : null, { name: name.trim(), ...config }),
    onSuccess: onCreated,
  })
  const created = mutation.data
  const invalid = (!daily && !granular) || (mode === 'selected' && selected.length === 0)

  const next = () => {
    if (!name.trim()) {
      setNameInvalid(true)
      return
    }
    setNameInvalid(false)
    if (isGlobal && !reviewing) setReviewing(true)
    else mutation.mutate()
  }

  return (
    <Dialog
      title={isGlobal ? (reviewing && !created ? 'Review Global API Key' : 'Generate Global API Key') : 'Generate API Key'}
      onClose={onClose}
      canClose={!mutation.isPending}
      wide={isGlobal}
      footer={
        created ? (
          <button type="button" onClick={onClose} className={BTN_PRIMARY}>
            Done
          </button>
        ) : (
          <>
            <button
              type="button"
              onClick={reviewing ? () => setReviewing(false) : onClose}
              disabled={mutation.isPending}
              className={BTN_SECONDARY}
            >
              {reviewing ? 'Back' : 'Cancel'}
            </button>
            <button type="button" disabled={mutation.isPending || invalid} onClick={next} className={BTN_PRIMARY}>
              {mutation.isPending ? 'Generating…' : isGlobal && !reviewing ? 'Review' : 'Generate'}
            </button>
          </>
        )
      }
    >
      {created ? (
        <KeyReveal rawKey={created.raw_key} warning="Copy this key now.">
          <p className="mt-2 text-xs text-text-secondary">
            Send it in the <code>x-api-key</code> request header. Store it securely — only the prefix (
            <code>{created.record.key_prefix}</code>•••) will be shown after this.
          </p>
        </KeyReveal>
      ) : reviewing ? (
        <div className="space-y-3">
          <p className="text-sm text-text-secondary">
            <strong className="text-text-primary">{name.trim()}</strong> will be able to read only these sites:
          </p>
          <ScopeReview config={config} labels={siteLabels(scope, inventory)} />
          {mutation.isError && <p className="text-sm text-error">Failed to generate key: {errorText(mutation.error)}</p>}
        </div>
      ) : (
        <div className="space-y-4">
          <div>
            <label htmlFor="new-key-name" className={LABEL}>
              Key label <span className="text-error">*</span>
            </label>
            <input
              id="new-key-name"
              value={name}
              maxLength={120}
              placeholder="e.g. External Dashboard Integration"
              onChange={(e) => {
                setName(e.target.value)
                if (e.target.value.trim()) setNameInvalid(false)
              }}
              className={`${INPUT} ${nameInvalid ? 'border-error' : ''}`}
              aria-invalid={nameInvalid}
              autoFocus
            />
            <p className={`mt-1 text-xs ${nameInvalid ? 'text-error' : 'text-text-secondary'}`}>
              {nameInvalid ? 'A key label is required.' : 'A label to help you identify this key later.'}
            </p>
          </div>
          <PlanAndEndpointFields
            idPrefix="new-key"
            tier={tier}
            setTier={setTier}
            daily={daily}
            setDaily={setDaily}
            granular={granular}
            setGranular={setGranular}
          />
          {scope.kind === 'client' ? (
            <SiteAccessFields
              name="new-key-site-mode"
              mode={mode}
              setMode={setMode}
              selected={selected}
              setSelected={setSelected}
              controllers={scope.controllers}
              allLabel="Full Client Access — all sites owned by this client"
            />
          ) : inventoryError ? (
            <p className="text-sm text-error">Failed to load sites.</p>
          ) : (
            <GlobalSitePicker inventory={inventory} selected={selected} setSelected={setSelected} />
          )}
          {mutation.isError && <p className="text-sm text-error">Failed to generate key: {errorText(mutation.error)}</p>}
        </div>
      )}
    </Dialog>
  )
}

function EditKeyDialog({
  scope,
  record,
  inventory,
  onClose,
  onSaved,
}: {
  scope: ApiKeyScope
  record: ApiKeyRecord
  inventory: SiteInventoryCompany[] | undefined
  onClose: () => void
  onSaved: () => void
}) {
  const [tier, setTier] = useState<SubscriptionTier>(record.subscription_tier)
  const [daily, setDaily] = useState(record.daily_access)
  const [granular, setGranular] = useState(record.granular_access)
  const [mode, setMode] = useState<SiteAccessMode>(record.site_access_mode === 'selected' ? 'selected' : 'all')
  const [selected, setSelected] = useState<string[]>(record.assigned_controller_ids)
  const mutation = useMutation({
    mutationFn: () =>
      updateApiKeyConfig(scope.kind === 'client' ? scope.clientId : null, record.id, record.config_version, {
        subscription_tier: tier,
        daily_access: daily,
        granular_access: granular,
        site_access_mode: mode,
        controller_ids: selected,
      }),
    onSuccess: () => {
      onSaved()
      onClose()
    },
    onError: (err) => {
      // Someone else saved first: reload the list so a reopened dialog
      // starts from what is actually stored.
      if (err instanceof ApiError && err.status === 409) onSaved()
    },
  })
  const invalid = (!daily && !granular) || (mode === 'selected' && selected.length === 0)
  return (
    <Dialog
      title={`Edit — ${record.name}`}
      onClose={onClose}
      canClose={!mutation.isPending}
      wide={scope.kind === 'global'}
      footer={
        <>
          <button type="button" onClick={onClose} disabled={mutation.isPending} className={BTN_SECONDARY}>
            Cancel
          </button>
          <button type="button" onClick={() => mutation.mutate()} disabled={mutation.isPending || invalid} className={BTN_PRIMARY}>
            {mutation.isPending ? 'Saving…' : 'Save'}
          </button>
        </>
      }
    >
      <div className="space-y-4">
        <p className="text-xs text-text-secondary">Changes apply to the key&apos;s next request. The key itself does not change.</p>
        <PlanAndEndpointFields
          idPrefix="edit-key"
          tier={tier}
          setTier={setTier}
          daily={daily}
          setDaily={setDaily}
          granular={granular}
          setGranular={setGranular}
        />
        {scope.kind === 'client' ? (
          <SiteAccessFields
            name="edit-site-mode"
            mode={mode}
            setMode={setMode}
            selected={selected}
            setSelected={setSelected}
            controllers={scope.controllers}
            allLabel="Full Client Access — all sites"
          />
        ) : (
          <GlobalSitePicker inventory={inventory} selected={selected} setSelected={setSelected} />
        )}
        {mode === 'selected' && selected.length === 0 && <p className="text-xs text-error">Select at least one site.</p>}
        {mutation.isError && <p className="text-sm text-error">Failed to update key: {errorText(mutation.error)}</p>}
      </div>
    </Dialog>
  )
}

const GRACE_OPTIONS = [
  { days: 0, label: 'None — revoke old key immediately' },
  { days: 3, label: '3 days' },
  { days: 7, label: '7 days (recommended)' },
  { days: 14, label: '14 days' },
  { days: 30, label: '30 days' },
]

function RotateDialog({
  clientId,
  record,
  onClose,
  onRotated,
}: {
  clientId: string | null
  record: ApiKeyRecord
  onClose: () => void
  onRotated: () => void
}) {
  const [graceDays, setGraceDays] = useState(7)
  const mutation = useMutation({
    mutationFn: () => rotateApiKey(clientId, record.id, graceDays),
    onSuccess: onRotated,
  })
  const result = mutation.data
  return (
    <Dialog
      title={`Rotate — ${record.name}`}
      onClose={onClose}
      canClose={!mutation.isPending}
      footer={
        result ? (
          <button type="button" onClick={onClose} className={BTN_PRIMARY}>
            Done
          </button>
        ) : (
          <>
            <button type="button" onClick={onClose} disabled={mutation.isPending} className={BTN_SECONDARY}>
              Cancel
            </button>
            <button type="button" onClick={() => mutation.mutate()} disabled={mutation.isPending} className={BTN_PRIMARY}>
              {mutation.isPending ? 'Rotating…' : 'Rotate Key'}
            </button>
          </>
        )
      }
    >
      {result ? (
        <KeyReveal rawKey={result.raw_key} warning="Copy the new key now.">
          <p className="mt-2 text-xs text-text-secondary">
            {result.grace_period_days > 0
              ? `Old key remains valid until ${result.grace_ends_at}. Delete it after migrating.`
              : 'Old key revoked immediately.'}
          </p>
        </KeyReveal>
      ) : (
        <div>
          <p className="mb-3 text-sm text-text-secondary">
            A new API key will be generated with the same plan, endpoints and sites. The current key remains valid during the grace
            period, giving integrations time to update.
          </p>
          <label htmlFor="rotate-grace-period" className={LABEL}>
            Grace Period
          </label>
          <select id="rotate-grace-period" value={graceDays} onChange={(e) => setGraceDays(Number(e.target.value))} className={INPUT}>
            {GRACE_OPTIONS.map((o) => (
              <option key={o.days} value={o.days}>
                {o.label}
              </option>
            ))}
          </select>
          <p className="mt-1 text-xs text-text-secondary">The old key stops working after this period.</p>
          {mutation.isError && <p className="mt-3 text-sm text-error">Failed to rotate key: {errorText(mutation.error)}</p>}
        </div>
      )}
    </Dialog>
  )
}
